import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { useNavigate } from 'react-router-dom';
import { normalizePlanName } from '@/utils/planName';

interface PlanSummaryCardProps {
  planData?: {
    name: string;
    type: string;
    price: string;
    next_billing: string | null;
    currency: string;
  };
  isLoading: boolean;
}

export const PlanSummaryCard: React.FC<PlanSummaryCardProps> = ({
  planData,
  isLoading
}) => {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  return (
    <Card
      className="border border-[#E5E7EB] rounded-[14px] overflow-hidden"
      style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}
    >
      {/* Header sem gradiente */}
      <div className="p-4 pb-3">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h2 className="text-[18px] font-semibold text-[#111827]">
              {isLoading ? translateInterfaceText("Carregando...") : translateInterfaceText("Plano Ativo — {value0}", { value0: (normalizePlanName(planData?.name) || 'Sem plano') })}
            </h2>
            <p className="text-[13px] text-[#6B7280] mt-0.5">
              {translateInterfaceText("Todos os recursos e benefícios para seu negócio")}</p>
          </div>
          <Button
            variant="outline"
            className="h-[38px] px-4 border-[#2563EB] text-[#2563EB] font-semibold rounded-[10px] hover:bg-[#2563EB] hover:text-white transition-colors"
            onClick={() => navigate('/checkout')}
          >
            {translateInterfaceText("Alterar plano")}</Button>
        </div>
      </div>

      <CardContent className="pt-0 pb-4 px-4">
        <div className="pt-3 border-t border-[#E5E7EB]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Valor mensal */}
            <div className="flex flex-col gap-1">
              <h3 className="text-[12px] font-normal text-[#6B7280] uppercase tracking-wide">{translateInterfaceText("Valor mensal")}</h3>
              <p className="text-[16px] font-semibold text-[#111827]">
                {isLoading ? '...' : planData?.price || 'R$ 0,00'}
                <span className="text-[13px] font-normal text-[#6B7280] ml-1">{translateInterfaceText("/ mês")}</span>
              </p>
            </div>

            {/* Próxima cobrança */}
            <div className="flex flex-col gap-1">
              <h3 className="text-[12px] font-normal text-[#6B7280] uppercase tracking-wide">{translateInterfaceText("Próxima cobrança")}</h3>
              <p className="text-[16px] font-semibold text-[#111827]">
                {isLoading ? '...' : planData?.next_billing || translateInterfaceText("Não definida")}
              </p>
            </div>

            {/* Dados do titular */}
            <div className="flex flex-col gap-1">
              <h3 className="text-[12px] font-normal text-[#6B7280] uppercase tracking-wide">{translateInterfaceText("Dados do titular")}</h3>
              <p className="text-[16px] font-semibold text-[#111827]">
                {isLoading ? '...' : `${user?.nome || ''} ${user?.sobrenome || ''}`.trim() || translateInterfaceText("Não informado")}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};